// Programmatic SEO dataset — symptom x occupation permutations.
// Pure JS (no JSX / no alias imports) so the sitemap generator script can import it.

export const SYMPTOMS = [
  {
    slug: "fatigue",
    name: "Fatigue",
    hint: "Persistent low energy and that 'running on empty' feeling",
    tips: [
      "A consistent sleep–wake window appears to help many people stabilise daytime energy.",
      "Short bursts of morning daylight are often consistent with better alertness rhythms.",
      "Balanced, iron-aware Indian meals (dal, greens, sprouts) may support steadier energy.",
    ],
  },
  {
    slug: "bloating",
    name: "Bloating",
    hint: "Post-meal heaviness and abdominal discomfort",
    tips: [
      "Eating a little slower appears to reduce how much air is swallowed with meals.",
      "A short 10-minute walk after meals is often consistent with easier digestion.",
      "Noticing which foods precede discomfort (a simple food journal) may reveal patterns.",
    ],
  },
  {
    slug: "insomnia",
    name: "Insomnia",
    hint: "Trouble falling or staying asleep",
    tips: [
      "A fixed wind-down ritual appears to signal the brain that sleep is approaching.",
      "Keeping screens off for the last hour is often consistent with faster sleep onset.",
      "An afternoon caffeine cut-off (roughly 2–3 pm) may reduce night-time restlessness.",
    ],
  },
  {
    slug: "acid-reflux",
    name: "Acid Reflux",
    hint: "Burning sensation or sourness after meals",
    tips: [
      "Smaller evening meals appear to reduce night-time discomfort for many people.",
      "Staying upright for 2–3 hours after eating is often consistent with fewer flare-ups.",
      "Noting personal trigger foods (spicy, fried, very late meals) may help you plan around them.",
    ],
  },
  {
    slug: "migraine",
    name: "Migraine",
    hint: "Recurring throbbing head pain episodes",
    tips: [
      "Regular meal timing and steady hydration appear to matter more than most people expect.",
      "Consistent sleep hours — even on weekends — are often consistent with fewer episodes.",
      "A simple trigger journal (light, screens, skipped meals) may help you spot your patterns.",
    ],
  },
  {
    slug: "anxiety",
    name: "Anxiety",
    hint: "Persistent worry, restlessness or a racing mind",
    tips: [
      "Slow breathing drills (like 4-4-4 box breathing) appear to settle the nervous system for many.",
      "Brief outdoor walks are often consistent with a calmer baseline mood.",
      "Writing worries down at a fixed 'worry time' may reduce how often they intrude.",
    ],
  },
  {
    slug: "back-pain",
    name: "Back Pain",
    hint: "Stiffness or ache in the lower or upper back",
    tips: [
      "Short movement breaks every 45–60 minutes appear to ease stiffness from sitting.",
      "Gentle daily stretching is often consistent with better back comfort over weeks.",
      "Screen-at-eye-level desk setups may reduce the load on the neck and upper back.",
    ],
  },
  {
    slug: "brain-fog",
    name: "Brain Fog",
    hint: "Hazy focus and slow recall during the day",
    tips: [
      "Steady hydration through the day appears to support clearer thinking.",
      "Single-tasking in short focused blocks is often consistent with sharper recall.",
      "Brief movement 'snacks' between tasks may refresh attention.",
    ],
  },
  {
    slug: "eye-strain",
    name: "Eye Strain",
    hint: "Tired, dry or burning eyes after long screen time",
    tips: [
      "The 20-20-20 habit — every 20 minutes, look at something 20 feet away for 20 seconds — appears to ease screen fatigue.",
      "Matching screen brightness to the room is often consistent with less end-of-day eye discomfort.",
      "Conscious blinking breaks may help; blink rates drop sharply during focused screen work.",
    ],
  },
  {
    slug: "neck-pain",
    name: "Neck Pain",
    hint: "Stiffness or ache across the neck and shoulders",
    tips: [
      "Raising screens and phones toward eye level appears to reduce the forward-head load.",
      "Slow shoulder rolls between tasks are often consistent with looser neck muscles.",
      "Alternating which side you carry bags or hold the phone may even out the strain.",
    ],
  },
  {
    slug: "stress-eating",
    name: "Stress Eating",
    hint: "Reaching for snacks when pressure rises",
    tips: [
      "A 'pause and sip water' ritual before snacking appears to separate hunger from tension.",
      "Protein- and fibre-rich regular meals are often consistent with fewer urgency cravings.",
      "Planned snack windows may reduce unplanned grazing during stressful stretches.",
    ],
  },
  {
    slug: "joint-stiffness",
    name: "Joint Stiffness",
    hint: "Morning stiffness or creaky, slow-to-warm-up joints",
    tips: [
      "A few minutes of gentle morning mobility appears to warm joints up for the day.",
      "Breaking up long still periods with short walks is often consistent with easier movement.",
      "Steady hydration may support the tissues that cushion your joints.",
    ],
  },
];

export const OCCUPATIONS = [
  {
    slug: "desk-workers",
    name: "Desk Workers",
    context: "long seated hours and back-to-back screen time",
  },
  {
    slug: "software-engineers",
    name: "Software Engineers",
    context: "deep-focus sprints, late deploys and irregular meals",
  },
  {
    slug: "college-students",
    name: "College Students",
    context: "irregular sleep, exam pressure and canteen food",
  },
  {
    slug: "night-shift-drivers",
    name: "Night Shift Drivers",
    context: "overnight driving, flipped sleep cycles and highway meals",
  },
  {
    slug: "call-center-agents",
    name: "Call Center Agents",
    context: "back-to-back calls, headset strain and fixed break windows",
  },
  {
    slug: "teachers",
    name: "Teachers",
    context: "long standing hours, vocal strain and skipped breaks",
  },
  {
    slug: "delivery-riders",
    name: "Delivery Riders",
    context: "long hours on two-wheelers, traffic stress and meals on the go",
  },
  {
    slug: "nurses",
    name: "Nurses",
    context: "rotating shifts, long rounds and missed meal breaks",
  },
  {
    slug: "retail-workers",
    name: "Retail Workers",
    context: "all-day standing, weekend rushes and unpredictable breaks",
  },
  {
    slug: "freelancers",
    name: "Freelancers",
    context: "client deadlines, irregular hours and working from bed or sofa",
  },
];

export const findSymptom = (slug) => SYMPTOMS.find((s) => s.slug === slug) || null;
export const findOccupation = (slug) => OCCUPATIONS.find((o) => o.slug === slug) || null;

// Parse "fix-{symptom}-for-{occupation}" → { symptom, occupation } or null.
export const parseFixSlug = (slug) => {
  if (!slug || !slug.startsWith("fix-")) return null;
  const parts = slug.slice(4).split("-for-");
  if (parts.length !== 2) return null;
  const symptom = findSymptom(parts[0]);
  const occupation = findOccupation(parts[1]);
  if (!symptom || !occupation) return null;
  return { symptom, occupation };
};

export const toolPath = (s, o) => `/tools/fix-${s.slug}-for-${o.slug}`;

export const allToolPaths = () =>
  SYMPTOMS.flatMap((s) => OCCUPATIONS.map((o) => toolPath(s, o)));

// Front-end lifestyle index from the two screening sliders.
// sleep: 3–10 hours, caffeine: 0–6 cups/day.
export const lifestyleIndex = (sleepHours, caffeineCups) => {
  const sleepScore = Math.min(100, Math.max(0, ((sleepHours - 3) / 7) * 100));
  const caffeinePenalty = Math.min(45, caffeineCups * 7.5);
  return Math.round(Math.min(98, Math.max(4, sleepScore - caffeinePenalty + 12)));
};

export const indexBand = (index) => {
  if (index >= 70) return { key: "steady", label: "Steady baseline", color: "#34d399" };
  if (index >= 40) return { key: "strained", label: "Strained baseline", color: "#fbbf24" };
  return { key: "overloaded", label: "Overloaded baseline", color: "#fb7185" };
};

// Career-context scorecard message (non-clinical, hedged).
export const scorecardMessage = (index, symptom, occupation) => {
  if (index >= 70) {
    return `Your inputs suggest a fairly steady baseline. For ${occupation.name.toLowerCase()}, a routine like yours appears to reflect decent recovery capacity around ${symptom.name.toLowerCase()}-related complaints — keeping it consistent may matter more than changing it.`;
  }
  if (index >= 40) {
    return `Your routine appears to reflect a strained rhythm — a pattern often consistent with ${occupation.context}. This is commonly associated with ${symptom.name.toLowerCase()}-type complaints, and small consistent tweaks may be worth exploring.`;
  }
  return `Your inputs are consistent with an overloaded routine — something many ${occupation.name.toLowerCase()} report during demanding stretches. This characteristic is often associated with ${symptom.name.toLowerCase()}-type complaints; gentle changes to sleep and stimulation timing may help.`;
};

// Per-page unique FAQ content (also fed into FAQPage JSON-LD for rich results).
export const buildFaq = (symptom, occupation) => [
  {
    q: `Why do ${occupation.name.toLowerCase()} often notice ${symptom.name.toLowerCase()}?`,
    a: `A workday shaped by ${occupation.context} is often consistent with ${symptom.name.toLowerCase()}-type complaints. ${symptom.hint} tends to show up when routines become repetitive — and patterns like this are usually lifestyle-linked rather than fixed.`,
  },
  {
    q: `What simple changes may help ${occupation.name.toLowerCase()} with ${symptom.name.toLowerCase()}?`,
    a: `${symptom.tips[0]} ${symptom.tips[1]}`,
  },
  {
    q: "How does the free lifestyle screening on this page work?",
    a: "Move two sliders — hours slept last night and daily caffeine intake — and your lifestyle index is calculated instantly in your browser with a scorecard written for your work context. Nothing is uploaded or stored, and no account is needed.",
  },
  {
    q: "Is this medical advice?",
    a: "No. SilentScan AI is an educational wellness simulator. It does not provide medical evaluations, clinical testing, or diagnoses. Verify all metrics against standard professional documentation and consult a qualified medical practitioner regarding any clinical symptoms.",
  },
];
